import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { ElementInsertionOption } from '../../elements/types';

interface UseAddElementSlotKeyboardNavigationArgs {
    isMenuOpen: boolean;
    insertionOptions: ElementInsertionOption[];
    onSelect: (option: ElementInsertionOption) => void;
    onFallbackKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}

interface UseAddElementSlotKeyboardNavigationResult {
    activeMenuOptionIndex: number | null;
    handleSlotKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
    handleMenuOptionMouseEnter: (optionIndex: number, isEnabled: boolean) => void;
}

function getNextEnabledOptionIndex(
    enabledOptionIndexes: number[],
    currentIndex: number | null,
    direction: 1 | -1
): number | null {
    if (enabledOptionIndexes.length === 0) {
        return null;
    }

    if (currentIndex === null) {
        return direction === 1
            ? enabledOptionIndexes[0]
            : enabledOptionIndexes[enabledOptionIndexes.length - 1];
    }

    const currentEnabledListIndex = enabledOptionIndexes.indexOf(currentIndex);
    if (currentEnabledListIndex === -1) {
        return direction === 1
            ? enabledOptionIndexes[0]
            : enabledOptionIndexes[enabledOptionIndexes.length - 1];
    }

    const nextEnabledListIndex =
        (currentEnabledListIndex + direction + enabledOptionIndexes.length) %
        enabledOptionIndexes.length;
    return enabledOptionIndexes[nextEnabledListIndex];
}

export function useAddElementSlotKeyboardNavigation({
    isMenuOpen,
    insertionOptions,
    onSelect,
    onFallbackKeyDown,
}: UseAddElementSlotKeyboardNavigationArgs): UseAddElementSlotKeyboardNavigationResult {
    const [activeMenuOptionIndex, setActiveMenuOptionIndex] = useState<number | null>(null);
    const activeMenuOptionIndexRef = useRef<number | null>(null);
    const enabledMenuOptionIndexes = useMemo(
        () =>
            insertionOptions
                .map((option, index) => (option.isEnabled ? index : -1))
                .filter((index) => index >= 0),
        [insertionOptions]
    );

    useEffect(() => {
        activeMenuOptionIndexRef.current = activeMenuOptionIndex;
    }, [activeMenuOptionIndex]);

    useEffect(() => {
        if (!isMenuOpen || !enabledMenuOptionIndexes.length) {
            setActiveMenuOptionIndex(null);
            return;
        }

        setActiveMenuOptionIndex((currentIndex) => {
            if (currentIndex !== null && enabledMenuOptionIndexes.includes(currentIndex)) {
                return currentIndex;
            }
            return enabledMenuOptionIndexes[0];
        });
    }, [isMenuOpen, enabledMenuOptionIndexes]);

    const handleMenuHotkey = useCallback(
        (
            event: Pick<KeyboardEvent<HTMLTextAreaElement>, 'key' | 'shiftKey' | 'preventDefault'>
        ) => {
            if (!isMenuOpen) {
                return false;
            }

            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveMenuOptionIndex((currentIndex) =>
                    getNextEnabledOptionIndex(
                        enabledMenuOptionIndexes,
                        currentIndex,
                        event.key === 'ArrowDown' ? 1 : -1
                    )
                );
                return true;
            }

            if (event.key === 'Enter' && !event.shiftKey) {
                const currentActiveIndex = activeMenuOptionIndexRef.current;
                const selectedOption =
                    currentActiveIndex !== null ? insertionOptions[currentActiveIndex] : null;
                if (!selectedOption || !selectedOption.isEnabled) {
                    return false;
                }

                event.preventDefault();
                onSelect(selectedOption);
                return true;
            }

            return false;
        },
        [enabledMenuOptionIndexes, insertionOptions, isMenuOpen, onSelect]
    );

    useEffect(() => {
        if (!isMenuOpen) {
            return undefined;
        }

        function handleWindowKeyDown(event: globalThis.KeyboardEvent): void {
            if (event.defaultPrevented) {
                return;
            }

            void handleMenuHotkey(event);
        }

        window.addEventListener('keydown', handleWindowKeyDown, true);
        return () => {
            window.removeEventListener('keydown', handleWindowKeyDown, true);
        };
    }, [handleMenuHotkey, isMenuOpen]);

    function handleSlotKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
        if (event.defaultPrevented) {
            return;
        }

        if (handleMenuHotkey(event)) {
            return;
        }

        onFallbackKeyDown(event);
    }

    function handleMenuOptionMouseEnter(optionIndex: number, isEnabled: boolean): void {
        if (!isEnabled) {
            return;
        }
        setActiveMenuOptionIndex(optionIndex);
    }

    return {
        activeMenuOptionIndex,
        handleSlotKeyDown,
        handleMenuOptionMouseEnter,
    };
}
